import { useData } from "../../context/DataContext";
import { Card, StatCard } from "../kit";
import { fmtINR, snapshot, overviewSeries, breakdown } from "../../lib/finance";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell,
} from "recharts";

const PIE = ["#4F46E5", "#059669", "#D97706", "#E11D48", "#0EA5E9", "#7C3AED", "#64748B", "#10B981"];

export default function Overview() {
  const { entries, clients } = useData();
  const s = snapshot(entries, clients);
  const series = overviewSeries(entries);
  const incPie = breakdown(entries, "income");
  const expPie = breakdown(entries, "expense");

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="kpi-total-income" label="This Month Income" value={fmtINR(s.inc)} tone="income" />
        <StatCard testid="kpi-total-expenses" label="This Month Expenses" value={fmtINR(s.exp)} tone="expense" />
        <StatCard testid="kpi-net-profit" label="Net Profit (Month)" value={fmtINR(s.net)} tone={s.net >= 0 ? "pos" : "neg"} />
        <StatCard testid="kpi-outstanding-dues" label="Client Balances Owed" value={fmtINR(s.totalReceivable)} tone="brand" />
      </div>

      <StatCard testid="kpi-total-earnings" label="Total Earnings (All-time)" value={fmtINR(s.totalEarnings)} tone="pos"
        sub={`Clients: ${fmtINR(s.clientEarnings)}  ·  Collabs: ${fmtINR(s.collabEarnings)}`} />

      <Card className="p-5">
        <h2 className="text-lg font-semibold text-slate-900 font-head mb-4">Monthly income vs. expenses</h2>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748b" }} />
              <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => "₹" + v / 1000 + "k"} />
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="income" name="Income" stroke="#059669" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="expenses" name="Expenses" stroke="#E11D48" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="net" name="Net Profit" stroke="#0F172A" strokeWidth={2} strokeDasharray="5 3" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid md:grid-cols-2 gap-6">
        <PieCard title="Income breakdown" data={incPie} />
        <PieCard title="Expense breakdown" data={expPie} />
      </div>
    </div>
  );
}

function PieCard({ title, data }) {
  return (
    <Card className="p-5">
      <h2 className="text-lg font-semibold text-slate-900 font-head mb-2">{title}</h2>
      {data.length === 0 ? (
        <p className="text-sm text-muted-foreground py-12 text-center">No data yet.</p>
      ) : (
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="45%" outerRadius={80} innerRadius={45} paddingAngle={2}>
                {data.map((entry, i) => <Cell key={entry.name} fill={PIE[i % PIE.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtINR(v)} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      )}
    </Card>
  );
}
